import { desktopSecureStorage, getPlatform, isTauri } from '@/lib/utils/tauri'
import { useOrganizationStore } from '@/store/organization.store'
import { env } from '@/utils/env'

import type {
  BudgetBreakdownResponse,
  BudgetExportPayload,
  BudgetFundingLinkWithContract,
  BudgetHistoryResponse,
  BudgetMarginSummary,
  BudgetSummary,
  BudgetTrueUpSide,
  BudgetVarianceRollup,
  BudgetVarianceRollupQuery,
  BurnChartResponse,
  CreateBudgetFundingLinkInput,
  CreateDraftInvoicePayload,
  CreateDraftInvoiceResponse,
  DraftInvoicePreview,
  EligibleFundingContract,
  MarginTrendResponse,
  ProfitabilityByRoleEnhancedResponse,
  ProfitabilityCostStatesResponse,
  ProfitabilityRevenueBreakdown,
  ProfitabilityRevenueRecognitionResponse,
  ReforecastBudgetPayload,
  SetOriginalBudgetPayload,
  UpdateBudgetFundingLinkInput,
} from '@scrambl/shared'

import { apiClient } from './api-client'

export interface ProjectBillDataQualityResponse {
  billAmountVisible: boolean
  reportingCurrency: string | null
  asOfDate?: string
  issues: {
    count: number
    counts: {
      MISSING_STORED_PRICE: number
      MISSING_BILL_CURRENCY: number
      DIFFERENT_BILL_CURRENCY: number
    }
    samples: Array<{
      timeEntryId: string
      entryDate: string
      reason:
        | 'MISSING_STORED_PRICE'
        | 'MISSING_BILL_CURRENCY'
        | 'DIFFERENT_BILL_CURRENCY'
    }>
    samplesTruncated: boolean
  } | null
}

// Money fields are null when the server masks them for the viewer (masked
// money is null, never 0). Render a dash for null; never coalesce into math.
export interface ProjectForecastBreakdownRow {
  id: string
  name: string
  plannedHours: number
  actualHours: number
  totalCost: number | null
  totalRevenue: number | null
  variance: number
  margin: number | null
}

export interface ProjectForecastMemberRow extends ProjectForecastBreakdownRow {
  phaseId?: string
  phaseName?: string
  categoryId?: string
  categoryName?: string
  remainingHours: number
  remainingCost: number | null
}

export interface ProjectForecastResponse {
  missingRates?: {
    source: 'time_entries' | 'allocations'
    field: 'cost' | 'bill'
    count: number
  }[]
  currency: string
  project: { id: string; name: string; pricingModel: string | null }
  budget: {
    totalHours: number | null
    totalCost: number | null
    totalRevenue: number | null
    marginTarget: number | null
    hoursUsedPercent: number | null
    costUsedPercent: number | null
  } | null
  totals: {
    actualHours: number
    plannedHours: number
    actualCost: number | null
    totalCost: number | null
    totalRevenue: number | null
    totalMargin: number | null
    marginPercent: number | null
  }
  memberBreakdown: ProjectForecastMemberRow[]
  phaseBreakdown: ProjectForecastBreakdownRow[]
  categoryBreakdown: ProjectForecastBreakdownRow[]
  columnVisibility?: { billAmount: boolean; costAmount: boolean }
}

export interface ProjectForecastSnapshot {
  unavailableReason?: string | null
  currency: string | null
  id: string
  projectId: string
  snapshotDate: string
  snapshotType: 'MANUAL' | 'AUTO'
  triggerEvent: string
  forecastCost: number | null
  forecastRevenue: number | null
  forecastMargin: number | null
  totalPlannedHours: number | null
  totalActualHours: number | null
}

export class ProjectBudgetPlan3Api {
  private static instance: ProjectBudgetPlan3Api | null = null

  static getInstance() {
    return (this.instance ??= new ProjectBudgetPlan3Api())
  }

  /**
   * Every project (or client) in scope with its fee-side budget, forecast and
   * actuals in one request — the Profitability variance table's data.
   */
  getVarianceRollup(
    params: BudgetVarianceRollupQuery = {},
    signal?: AbortSignal,
  ): Promise<BudgetVarianceRollup> {
    const query = new URLSearchParams()
    if (params.mode) query.append('mode', params.mode)
    if (params.entityIds?.length)
      query.append('entityIds', params.entityIds.join(','))
    if (params.projectIds?.length)
      query.append('projectIds', params.projectIds.join(','))
    if (params.from && params.to) {
      query.append('from', params.from)
      query.append('to', params.to)
    }
    // Agent labor ledger: per-row agent split + leverage (ignored server-side
    // for orgs without the ledger).
    if (params.includeAgentSplit) query.append('include', 'agentSplit')
    const suffix = query.toString() ? `?${query.toString()}` : ''
    return apiClient(`/member-resources/budget/variance${suffix}`, {
      method: 'GET',
      signal,
    })
  }

  getBudgetSummary(projectId: string): Promise<BudgetSummary> {
    return apiClient(`/member-resources/projects/${projectId}/budget/summary`, {
      method: 'GET',
    })
  }

  getBurnChart(
    projectId: string,
    params?: {
      periodStart?: string
      periodEnd?: string
      side?: 'fee' | 'cost' | 'hours'
    },
  ): Promise<BurnChartResponse> {
    const query = new URLSearchParams()
    if (params?.periodStart) query.append('periodStart', params.periodStart)
    if (params?.periodEnd) query.append('periodEnd', params.periodEnd)
    if (params?.side) query.append('side', params.side)
    const qs = query.toString()
    return apiClient(
      `/member-resources/projects/${projectId}/budget/burn-chart${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    )
  }

  getBudgetBreakdown(
    projectId: string,
    params?: {
      groupBy?: 'member' | 'role' | 'phase'
      periodStart?: string
      periodEnd?: string
      granularity?: 'total' | 'monthly'
      side?: 'fee' | 'cost' | 'hours'
    },
  ): Promise<BudgetBreakdownResponse> {
    const query = new URLSearchParams()
    if (params?.groupBy) query.append('groupBy', params.groupBy)
    if (params?.periodStart) query.append('periodStart', params.periodStart)
    if (params?.periodEnd) query.append('periodEnd', params.periodEnd)
    if (params?.granularity) query.append('granularity', params.granularity)
    if (params?.side) query.append('side', params.side)
    const qs = query.toString()
    return apiClient(
      `/member-resources/projects/${projectId}/budget/breakdown${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    )
  }

  getBudgetHistory(
    projectId: string,
    params?: { page?: number; limit?: number },
  ): Promise<BudgetHistoryResponse> {
    const query = new URLSearchParams()
    if (params?.page) query.append('page', String(params.page))
    if (params?.limit) query.append('limit', String(params.limit))
    const qs = query.toString()
    return apiClient(
      `/member-resources/projects/${projectId}/budget/history${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    )
  }

  setOriginalBudget(
    projectId: string,
    payload: SetOriginalBudgetPayload,
  ): Promise<unknown> {
    return apiClient(
      `/member-resources/projects/${projectId}/budget/original`,
      {
        method: 'POST',
        body: payload,
      },
    )
  }

  reforecastBudget(
    projectId: string,
    payload: ReforecastBudgetPayload,
  ): Promise<unknown> {
    return apiClient(
      `/member-resources/projects/${projectId}/budget/reforecast`,
      {
        method: 'POST',
        body: payload,
      },
    )
  }

  // Phase 3: lifecycle

  closeBudget(projectId: string): Promise<{
    status: 'CLOSED'
    closedAt: string
    trueUp: { sides: BudgetTrueUpSide[]; margin: BudgetMarginSummary | null }
  }> {
    return apiClient(`/member-resources/projects/${projectId}/budget/close`, {
      method: 'POST',
    })
  }

  reopenBudget(projectId: string): Promise<{ status: 'ACTIVE' }> {
    return apiClient(`/member-resources/projects/${projectId}/budget/reopen`, {
      method: 'POST',
    })
  }

  // Phase 2: contract funding links (budget-to-contract spine)

  getFundingLinks(projectId: string): Promise<BudgetFundingLinkWithContract[]> {
    return apiClient(
      `/member-resources/projects/${projectId}/budget/funding-links`,
      {
        method: 'GET',
      },
    )
  }

  createFundingLink(
    projectId: string,
    payload: CreateBudgetFundingLinkInput,
  ): Promise<BudgetFundingLinkWithContract> {
    return apiClient(
      `/member-resources/projects/${projectId}/budget/funding-links`,
      {
        method: 'POST',
        body: payload,
      },
    )
  }

  updateFundingLink(
    projectId: string,
    linkId: string,
    payload: UpdateBudgetFundingLinkInput,
  ): Promise<BudgetFundingLinkWithContract> {
    return apiClient(
      `/member-resources/projects/${projectId}/budget/funding-links/${linkId}`,
      { method: 'PUT', body: payload },
    )
  }

  deleteFundingLink(
    projectId: string,
    linkId: string,
  ): Promise<{ success: true }> {
    return apiClient(
      `/member-resources/projects/${projectId}/budget/funding-links/${linkId}`,
      { method: 'DELETE' },
    )
  }

  getEligibleContracts(projectId: string): Promise<EligibleFundingContract[]> {
    return apiClient(
      `/member-resources/projects/${projectId}/budget/eligible-contracts`,
      { method: 'GET' },
    )
  }

  exportBudget(
    projectId: string,
    payload: BudgetExportPayload,
  ): Promise<{ jobId: string }> {
    return apiClient(`/member-resources/projects/${projectId}/budget/export`, {
      method: 'POST',
      body: payload,
    })
  }

  getExportJobStatus(
    projectId: string,
    jobId: string,
  ): Promise<{
    status: 'PENDING' | 'READY' | 'ERROR'
    downloadUrl?: string
    error?: string
  }> {
    return apiClient(
      `/member-resources/projects/${projectId}/budget/export/jobs/${jobId}`,
      { method: 'GET' },
    )
  }

  getExportDownloadUrl(projectId: string, jobId: string): string {
    return `/member-resources/projects/${projectId}/budget/export/jobs/${jobId}/download`
  }

  async downloadExportJob(
    projectId: string,
    jobId: string,
  ): Promise<{ blob: Blob; filename: string }> {
    const endpoint = `/member-resources/projects/${projectId}/budget/export/jobs/${jobId}/download`
    const { currentOrganization } = useOrganizationStore.getState()

    const headers: Record<string, string> = {
      Accept: 'application/octet-stream',
      'x-platform': getPlatform(),
    }

    if (currentOrganization?.id) {
      headers['x-organization-id'] = currentOrganization.id
      if (currentOrganization.slug)
        headers['x-organization-slug'] = currentOrganization.slug
    }

    if (isTauri()) {
      try {
        const tokens = await desktopSecureStorage.getTokens()
        if (tokens?.accessToken)
          headers['Authorization'] = `Bearer ${tokens.accessToken}`
      } catch {
        // desktop auth unavailable — proceed with cookie auth
      }
    }

    const response = await fetch(`${env.apiUrl}${endpoint}`, {
      method: 'GET',
      credentials: 'include',
      headers,
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(
        `Request failed: ${response.status} ${response.statusText} - ${errorText}`,
      )
    }

    const disposition = response.headers.get('content-disposition') ?? ''
    const filenameMatch = disposition.match(/filename="([^"]+)"/)
    const filename = filenameMatch?.[1] ?? `budget-export.bin`

    return { blob: await response.blob(), filename }
  }

  getDraftInvoicePreview(
    projectId: string,
    params?: Record<string, string>,
  ): Promise<DraftInvoicePreview> {
    const query = params ? new URLSearchParams(params).toString() : ''
    return apiClient(
      `/member-resources/projects/${projectId}/draft-invoice/preview${query ? `?${query}` : ''}`,
      { method: 'GET' },
    )
  }

  createDraftInvoice(
    projectId: string,
    payload: CreateDraftInvoicePayload,
  ): Promise<CreateDraftInvoiceResponse> {
    return apiClient(`/member-resources/projects/${projectId}/draft-invoice`, {
      method: 'POST',
      body: payload,
    })
  }

  getProfitabilityByRoleEnhanced(
    projectId: string,
  ): Promise<ProfitabilityByRoleEnhancedResponse> {
    return apiClient(
      `/member-resources/projects/${projectId}/profitability/by-role-enhanced`,
      { method: 'GET' },
    )
  }

  getRevenueBreakdown(
    projectId: string,
  ): Promise<ProfitabilityRevenueBreakdown> {
    return apiClient(
      `/member-resources/projects/${projectId}/profitability/revenue-breakdown`,
      { method: 'GET' },
    )
  }

  getRevenueRecognition(
    projectId: string,
    params?: { asOfDate?: string },
  ): Promise<ProfitabilityRevenueRecognitionResponse> {
    const query = new URLSearchParams()
    if (params?.asOfDate) query.append('asOfDate', params.asOfDate)
    const qs = query.toString()
    return apiClient(
      `/member-resources/projects/${projectId}/profitability/revenue-recognition${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    )
  }

  getBillDataQuality(
    projectId: string,
  ): Promise<ProjectBillDataQualityResponse> {
    return apiClient(
      `/member-resources/projects/${projectId}/profitability/bill-data-quality`,
      { method: 'GET' },
    )
  }

  getCostStates(
    projectId: string,
    params?: { asOfDate?: string },
  ): Promise<ProfitabilityCostStatesResponse> {
    const query = new URLSearchParams()
    if (params?.asOfDate) query.append('asOfDate', params.asOfDate)
    const qs = query.toString()
    return apiClient(
      `/member-resources/projects/${projectId}/profitability/cost-states${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    )
  }

  getMarginTrend(
    projectId: string,
    params?: {
      bucket?: 'week' | 'month'
      periodStart?: string
      periodEnd?: string
    },
  ): Promise<MarginTrendResponse> {
    const query = new URLSearchParams()
    if (params?.bucket) query.append('bucket', params.bucket)
    if (params?.periodStart) query.append('periodStart', params.periodStart)
    if (params?.periodEnd) query.append('periodEnd', params.periodEnd)
    const qs = query.toString()
    return apiClient(
      `/member-resources/projects/${projectId}/profitability/margin-trend${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    )
  }

  getForecast(projectId: string): Promise<ProjectForecastResponse> {
    return apiClient(`/member-resources/projects/${projectId}/forecast`, {
      method: 'GET',
    })
  }

  getForecastSnapshots(
    projectId: string,
    limit = 20,
  ): Promise<ProjectForecastSnapshot[]> {
    return apiClient(
      `/member-resources/projects/${projectId}/forecast/snapshots?limit=${limit}`,
      { method: 'GET' },
    )
  }

  takeForcastSnapshot(projectId: string): Promise<ProjectForecastSnapshot> {
    return apiClient(
      `/member-resources/projects/${projectId}/forecast/snapshot`,
      { method: 'POST' },
    )
  }
}

export const projectBudgetPlan3Api = ProjectBudgetPlan3Api.getInstance()
